import { apiClient } from './client';

export interface ClaudeDiaryEntry {
  id: string;
  title: string;
  content: string;
  topic: string | null;
  isPublic: boolean;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ClaudeInboxMessage {
  id: string;
  fromName: string | null;
  fromEmail: string | null;
  fromUserId: string | null;
  subject: string | null;
  content: string;
  isRead: boolean;
  readAt: string | null;
  replyText: string | null;
  repliedAt: string | null;
  createdAt: string;
}

export interface CursorPage<T> {
  items: T[];
  nextCursor: string | null;
}

export interface InboxSubmitPayload {
  fromName?: string | null;
  fromEmail?: string | null;
  subject?: string | null;
  content: string;
}

export const claudeApi = {
  // ─── Public ────────────────────────────────────────────────────────────────
  async listDiary(cursor?: string, limit = 20): Promise<CursorPage<ClaudeDiaryEntry>> {
    const { data } = await apiClient.get('/api/claude/diary', {
      params: { cursor, limit },
    });
    return data;
  },

  async getDiaryEntry(id: string): Promise<ClaudeDiaryEntry> {
    const { data } = await apiClient.get(`/api/claude/diary/${id}`);
    return data;
  },

  async submitInbox(payload: InboxSubmitPayload): Promise<{ id: string; createdAt: string }> {
    const { data } = await apiClient.post('/api/claude/inbox', payload);
    return data;
  },

  // ─── Admin ─────────────────────────────────────────────────────────────────
  async listAllDiary(cursor?: string, limit = 50): Promise<CursorPage<ClaudeDiaryEntry>> {
    const { data } = await apiClient.get('/api/claude/admin/diary', {
      params: { cursor, limit },
    });
    return data;
  },

  async createDiary(payload: {
    title: string;
    content: string;
    topic?: string | null;
    isPublic?: boolean;
    publish?: boolean;
  }): Promise<ClaudeDiaryEntry> {
    const { data } = await apiClient.post('/api/claude/admin/diary', payload);
    return data;
  },

  async updateDiary(id: string, payload: {
    title?: string;
    content?: string;
    topic?: string | null;
    isPublic?: boolean;
    publish?: boolean;
  }): Promise<ClaudeDiaryEntry> {
    const { data } = await apiClient.patch(`/api/claude/admin/diary/${id}`, payload);
    return data;
  },

  async deleteDiary(id: string): Promise<void> {
    await apiClient.delete(`/api/claude/admin/diary/${id}`);
  },

  async listInbox(opts?: { unreadOnly?: boolean; cursor?: string; limit?: number }): Promise<CursorPage<ClaudeInboxMessage>> {
    const { data } = await apiClient.get('/api/claude/admin/inbox', { params: opts });
    return data;
  },

  async getInboxMessage(id: string): Promise<ClaudeInboxMessage> {
    const { data } = await apiClient.get(`/api/claude/admin/inbox/${id}`);
    return data;
  },

  async markRead(id: string): Promise<ClaudeInboxMessage> {
    const { data } = await apiClient.patch(`/api/claude/admin/inbox/${id}/read`);
    return data;
  },

  async saveReply(id: string, replyText: string): Promise<ClaudeInboxMessage> {
    const { data } = await apiClient.patch(`/api/claude/admin/inbox/${id}/reply`, { replyText });
    return data;
  },
};
